import { inject, Injectable } from '@angular/core';
import { AppGlobalActions } from '@app/_store/actions/global.actions';
import { selectUserInfo } from '@app/_store/features/user-info/user-info.selectors';
import { selectUserPlan } from '@app/_store/features/user-plan/user-plan.selectors';
import { AppDataConfig } from '@core/configs/app-data.config';
import { AppDataContainers } from '@core/models/interfaces/app-data/app-data';
import { AppDataFetchingService } from '@core/services/data-processing/app-data-fetching.service';
import { StorageService } from '@core/services/storages/storage.service';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  exhaustMap,
  map,
  of,
  tap,
} from 'rxjs';

@Injectable()
export class AppGlobalEffects {
  private actions$ = inject(Actions);

  /**
   * R116. The tag is spread in only when there is one, so an untagged load stays
   * a two-key action in the devtools log instead of carrying `undefined` around.
   */
  private tag(correlationId?: string): { correlationId?: string } {
    return correlationId ? { correlationId } : {};
  }

  private storage = inject(StorageService);
  private appDataFetching = inject(AppDataFetchingService);
  private store = inject(Store);
  private mixpanel = inject(MixpanelService);

  /**
   * Mixpanel super-properties, set however the app data arrived.
   *
   * These were registered inside `processAppData`'s `userInfo` and
   * `userPlanState` cases, which only run when a load actually processes those
   * containers. A session that starts from the IndexedDB cache dispatches
   * `loadAppDataSuccess` without going through processing at all, so every event
   * it sent carried no Activation, Registration Date, Registration Platform or
   * isPremium. Here it fires on both paths — and a data-processing service no
   * longer reaches for the analytics client.
   */
  registerAnalyticsProperties$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppGlobalActions.loadAppDataSuccess),
        concatLatestFrom(() => [
          this.store.select(selectUserInfo),
          this.store.select(selectUserPlan),
        ]),
        tap(([, userInfo, userPlan]) => {
          if (userInfo) {
            this.mixpanel.register({
              Activation: userInfo.isActivated,
              'Registration Date': userInfo.registrationDate,
              'Registration Platform': userInfo.registrationPlatform,
            });
          }

          if (userPlan?.planDetails?.plan) {
            this.mixpanel.register({
              isPremium: userPlan.planDetails.plan !== 'basic',
            });
          }
        })
      ),
    { dispatch: false }
  );

  initAppState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppGlobalActions.loadAppData),
      // R45. Destructured rather than forwarded whole: the action used to be
      // passed straight through as the payload, `type` and all.
      concatMap(({ fields, reset, correlationId }) =>
        this.appDataFetching.loadAppData({ fields, reset }).pipe(
          // R121. Continuation is driven by what the response actually left
          // undelivered, not by a guess made from storage afterwards. When the
          // response is complete — which an anchored delta usually is — no
          // second round is asked for at all.
          concatMap(({ state, unfinished }) =>
            Object.keys(unfinished).length
              ? [
                  // R42. The continuation is announced *before* the success, so
                  // the in-flight count goes 1 → 2 → 1 rather than 1 → 0 → 1.
                  // Measured on a live refresh: with the success first, the count
                  // touched zero for a millisecond between the two actions, and
                  // `store.select` notifies synchronously — long enough for the
                  // Finicity modal's `filter(loading => !loading), take(1)`, its
                  // wait at the time, to fire and report a connected account
                  // mid-refresh. Nothing here reads state the success writes:
                  // `unfinished` comes from the response and the storage anchors
                  // were written before it. `reloadAndWaitForAllPages` relies on
                  // the order too: it learns of the continuation from this
                  // announcement, so it has to come before the reply.
                  AppGlobalActions.loadAppBatchedContainers({
                    fields: unfinished,
                    // POC-9571. The continuation carries the load's tag, so its
                    // reply does as well, and a caller can wait for its own data
                    // to be complete rather than for every load in flight.
                    ...this.tag(correlationId),
                  }),
                  // R116. The first reply carries the tag as well, and
                  // `reloadAndWait` still answers on it — exactly when it was
                  // answered before correlation existed. Waiting for the
                  // continuation is `reloadAndWaitForAllPages`, opted into by the
                  // connect flow alone: moving every caller onto it is a
                  // behaviour change nobody has measured yet.
                  AppGlobalActions.loadAppDataSuccess({
                    globalState: state,
                    ...this.tag(correlationId),
                  }),
                ]
              : [
                  AppGlobalActions.loadAppDataSuccess({
                    globalState: state,
                    ...this.tag(correlationId),
                  }),
                ]
          ),
          catchError((error) => {
            console.error('Error loading app state:', error);
            return of(
              AppGlobalActions.loadAppDataFailure(this.tag(correlationId))
            );
          })
        )
      )
    )
  );

  initAppStateIfNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppGlobalActions.loadAppDataIfNeeded),
      exhaustMap(() =>
        this.storage.getAllFields().pipe(
          map((globalState) => {
            if (
              globalState?.profile?.object &&
              Object.keys(globalState.profile.object).length > 0
            ) {
              return [
                // `fromCache` because nothing was requested — this is IndexedDB
                // being announced. Without it the counter decrements a request it
                // never counted, and any load actually in flight is reported as
                // finished: measured, a `loadAppData` still 600 ms from answering
                // was shown as idle the moment a route guard ran.
                AppGlobalActions.loadAppDataSuccess({
                  globalState,
                  fromCache: true,
                }),
                // Every batched container, not only the ones the cache happens
                // to hold. Passing `globalState` meant a container missing from
                // IndexedDB was never asked for: hydration issued no request at
                // all and the list stayed empty for the rest of the session.
                AppGlobalActions.loadAppBatchedContainers({
                  fields: Object.fromEntries(
                    AppDataConfig.batchedContainers.map((key) => [
                      key,
                      globalState[key] ?? {},
                    ])
                  ),
                }),
              ];
            }
            return [AppGlobalActions.loadAppData({})];
          }),
          concatMap((actions) => actions),
          catchError(() => of(AppGlobalActions.loadAppDataFailure({})))
        )
      )
    )
  );

  loadAppBatchedContainers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppGlobalActions.loadAppBatchedContainers),
      // `concatMap`, not `exhaustMap`. R42 counts this action as a load in flight
      // and balances it with the reply below, but `exhaustMap` discards an action
      // that arrives while a round is running — before the body that would reply.
      // The increment happened, the reply never could, and the count stayed up for
      // the rest of the session. Measured on the 15 414-transaction account with
      // two reset reloads 2 s apart: 2 rounds dispatched, 0 concluded, the counter
      // ended at 2 and never came down — ⌘E refuses to refresh on that flag. A
      // discarded round is also a connect modal that never closes: it waits for
      // its own continuation's reply (POC-9571). Queueing costs a repeated page or
      // two; a reply that never comes costs the feature.
      concatMap(({ fields, correlationId }) =>
        this.appDataFetching.loadBatchedContainers(fields).pipe(
          // R42. Every round concludes, one way or the other. It used to be
          // `filter(keys > 0)` and nothing else, which met R114's requirement —
          // no empty `loadAppDataSuccess`, because that invalidates all 23
          // feature states — but at the price of ending in silence, so the
          // loading flag could never be balanced against the request.
          // `defaultIfEmpty` covers the source completing without a value.
          defaultIfEmpty({} as AppDataContainers),
          map((loaded) =>
            Object.keys(loaded).length
              ? AppGlobalActions.loadAppDataSuccess({
                  globalState: loaded,
                  ...this.tag(correlationId),
                })
              : AppGlobalActions.loadAppBatchedContainersDone(
                  this.tag(correlationId)
                )
          ),
          catchError((error) => {
            console.error('Error loading app state:', error);
            return of(
              AppGlobalActions.loadAppDataFailure(this.tag(correlationId))
            );
          })
        )
      )
    )
  );
}
